/**
 * The delivery layer (tasks 14.3, 14.4, 14.5, 14.6).
 *
 * It wraps the plain HTTP content source with the four things that make the
 * game work on a train:
 *
 *   1. **Cached fallback for the catalog and the small documents.** A launch
 *      with no network reads the last good copy and says that it did.
 *   2. **Digest verification before load or cache.** A package is bytes until
 *      it hashes to what the catalog says it should; a mismatch is discarded,
 *      retried once, and then reported as something to retry rather than as a
 *      mission that is broken.
 *   3. **`minEngineVersion` refusal.** A package that needs a newer build is
 *      refused whole, with "update the game", never partially loaded.
 *   4. **Retention.** A package that has been played is kept; eviction gives up
 *      never-played ones first.
 *
 * Every fetch is same-origin under the distribution's `content/` prefix. This
 * layer adds no host, no header and no credential to the request the shell was
 * already making — the client still talks to nobody but its own origin.
 */
import { ENGINE_VERSION } from '@managing-up/engine';
import type { CareerPerks, DifficultyProfiles, LevelPackage } from '@managing-up/schema';
import { DEFAULT_MISSION_KIND, parseCatalog, type Catalog, type CatalogEntry } from '../catalog.js';
import { needsNewerThan } from '../versions.js';
import {
  indexedDbContentCache,
  nullContentCache,
  type CachedPack,
  type ContentCache,
} from './cache.js';
import { canVerify, DigestMismatchError, digestMatches, sha256OfText } from './digest.js';

/** Thrown when a package needs a newer build than the one running. */
export class EngineTooOldError extends Error {
  readonly required: string;
  readonly running: string;

  constructor(what: string, required: string, running: string) {
    super(
      `${what} needs version ${required} of the game. You are running version ${running}. Update the game to play it.`,
    );
    this.name = 'EngineTooOldError';
    this.required = required;
    this.running = running;
  }
}

/**
 * Where a catalog came from, which is what the mission list has to explain.
 *
 * `device` is the last resort: no network and no cached catalog, so the list is
 * reconstructed from the packages this device is actually holding. It is the
 * shortest possible list and it is honest about being one.
 */
export type CatalogOrigin = 'network' | 'cache' | 'device';

export interface CatalogResult {
  readonly catalog: Catalog;
  readonly origin: CatalogOrigin;
  /** Why the network copy could not be had. Set whenever `origin` is not `network`. */
  readonly reason: string | null;
}

/** Everything the launch surface loads, behind one interface so tests can stub it. */
export interface ContentSource {
  catalog(): Promise<CatalogResult>;
  profiles(): Promise<DifficultyProfiles>;
  perks(): Promise<CareerPerks>;
  /** The calibration report, or `null` for a mission that has none committed. */
  calibration(entry: CatalogEntry): Promise<CalibrationReport | null>;
  pack(entry: CatalogEntry): Promise<LevelPackage>;
  /**
   * A package by its own URL rather than by catalog entry.
   *
   * This is what makes "resume on the cached original" possible after the
   * catalog has moved on (task 13.5): published packages are immutable and
   * permanently addressable, so a run that recorded where its package came from
   * can fetch that exact version back — from this device's cache when the
   * network is gone, which is the case that matters.
   */
  packFrom(url: string, label: string): Promise<LevelPackage>;
  /**
   * Records that a run has actually opened on this package, which is what
   * protects it from eviction (task 14.6). Called when a mission starts, not
   * when its briefing is read.
   */
  markPlayed(entry: CatalogEntry): Promise<void>;
}

/**
 * The slice of a calibration report the launch surface reads: the measured
 * duration estimate the difficulty screen is required to show, and the solver
 * par a scored run needs. The full report is the playtest harness's; this reads
 * it rather than redefining it.
 */
export interface CalibrationReport {
  readonly agent?: {
    readonly measured?: boolean | undefined;
    readonly profiles?: readonly {
      readonly profileId: string;
      readonly estimatedMinutes?: number | null | undefined;
      readonly estimatedMinutesRange?: readonly number[] | null | undefined;
      readonly targetBand?: { readonly estimatedMinutes?: readonly number[] } | undefined;
    }[];
  };
  readonly solver?: {
    readonly measured?: boolean | undefined;
    readonly profiles?: readonly {
      readonly profileId: string;
      readonly parSends?: number | null | undefined;
      readonly parPhases?: number | null | undefined;
    }[];
  };
}

export const DEFAULT_CONTENT_BASE = '/content/';

/** The bytes of a content object, and nothing interpreted about them. */
export interface FetchedText {
  readonly text: string;
  readonly url: string;
}

/** How the delivery layer reaches the network. Swapped wholesale in tests. */
export type TextFetcher = (url: string, what: string) => Promise<FetchedText>;

/** Same-origin `fetch`, with no credentials and no headers of its own. */
export const httpFetcher: TextFetcher = async (url, what) => {
  const response = await fetch(url, { credentials: 'omit' });
  if (!response.ok) {
    throw new Error(
      `Could not load ${what}. The server returned status ${String(response.status)}.`,
    );
  }
  return { text: await response.text(), url };
};

export interface DeliveryOptions {
  readonly base?: string;
  readonly cache?: ContentCache;
  readonly fetchText?: TextFetcher;
  readonly engineVersion?: string;
  /** Injected so a test can assert what was written and when. */
  readonly now?: () => string;
}

/**
 * A catalog rebuilt from the packages on this device.
 *
 * With no catalog and no network, "present cached missions" is still a
 * requirement (`offline-delivery` spec, "Catalog fetch with offline fallback"),
 * and everything an entry needs is inside the package it describes: a manifest
 * carries the title, the company, the order and the prerequisites. So a device
 * that has played a mission can still list and replay it having never been
 * given a catalog at all.
 *
 * The premise is one of two things a manifest does not have. It is a line for
 * the mission list rather than anything the game reads, so it is left empty
 * rather than invented.
 *
 * The other is `kind`, which is a catalog fact and not a package one. With no
 * catalog to read it from, every rebuilt entry is a campaign mission — the same
 * reading a client that predates the field gives, and the same default
 * `parseCatalog` applies to an entry that omits it.
 */
async function catalogFromCachedPacks(cache: ContentCache): Promise<Catalog> {
  const missions: CatalogEntry[] = [];
  for (const held of await cache.listPacks()) {
    try {
      const manifest = (JSON.parse(held.text) as LevelPackage).manifest;
      missions.push({
        id: manifest.id,
        title: manifest.title,
        company: manifest.company,
        premise: '',
        order: manifest.order,
        url: held.url,
        packVersion: manifest.packVersion,
        sha256: held.sha256,
        requires: manifest.requires,
        minEngineVersion: manifest.minEngineVersion,
        kind: DEFAULT_MISSION_KIND,
      });
    } catch {
      // A package that no longer parses is one this device cannot offer.
    }
  }
  return { catalogVersion: '0', missions };
}

/**
 * Reads content from this origin, verifying and caching on the way through.
 *
 * `base` resolves everything else: package URLs are relative to the catalog, so
 * the repository layout (`<id>/pack.json`) and the published layout
 * (`packs/<id>@<version>.json`) both work with no application change.
 */
export function deliveringContentSource(options: DeliveryOptions = {}): ContentSource {
  const base = options.base ?? DEFAULT_CONTENT_BASE;
  const cache = options.cache ?? indexedDbContentCache();
  const fetchText = options.fetchText ?? httpFetcher;
  const engineVersion = options.engineVersion ?? ENGINE_VERSION;
  const now = options.now ?? (() => new Date().toISOString());

  const catalogUrl = new URL(
    base + 'catalog.json',
    globalThis.location?.href ?? 'http://localhost/',
  );
  const at = (url: string): string => new URL(url, catalogUrl).toString();

  /**
   * A small document — the catalog, the profiles, a calibration report.
   *
   * Network first, then the cached copy. Network-first rather than
   * cache-first because these are the documents that *change*: a stale profile
   * document would silently keep a player on last week's difficulty tuning, and
   * they are a few kilobytes, so the cost of asking is nothing.
   */
  const document = async (
    key: string,
    url: string,
    what: string,
  ): Promise<{ text: string; fromCache: boolean; reason: string | null }> => {
    try {
      const { text } = await fetchText(url, what);
      await cache.writeDocument(key, text, now());
      return { text, fromCache: false, reason: null };
    } catch (cause) {
      const cached = await cache.readDocument(key);
      if (cached === null) throw cause;
      return {
        text: cached.text,
        fromCache: true,
        reason: cause instanceof Error ? cause.message : `Could not load ${what}.`,
      };
    }
  };

  const parsed = async <T>(key: string, url: string, what: string): Promise<T> =>
    JSON.parse((await document(key, url, what)).text) as T;

  /** Refuses a package this build is too old for, before anything is parsed. */
  const assertEngine = (what: string, required: string | undefined): void => {
    if (required === undefined) return;
    if (needsNewerThan(required, engineVersion)) {
      throw new EngineTooOldError(what, required, engineVersion);
    }
  };

  /**
   * Fetches a package and proves it is the one the catalog named.
   *
   * Retried once on a mismatch, because the overwhelmingly likely cause of one
   * is a truncated or intercepted response rather than a genuinely corrupt
   * object, and a single retry costs one request. A second mismatch is
   * reported: at that point something is wrong that a third attempt will not
   * fix, and the player is owed a retry button rather than a spinner.
   */
  const fetchVerified = async (
    url: string,
    what: string,
    expected: string,
  ): Promise<{ text: string; sha256: string }> => {
    let last: DigestMismatchError | null = null;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      const { text } = await fetchText(url, what);
      // A development host without a secure context cannot digest. Refusing to
      // play there would be worse than playing unverified, and the shipped
      // distribution is HTTPS-only, so this is a development-only softening.
      if (!canVerify()) return { text, sha256: '' };
      const actual = await sha256OfText(text);
      if (digestMatches(expected, actual)) return { text, sha256: actual };
      last = new DigestMismatchError(url, expected, actual);
    }
    throw last ?? new DigestMismatchError(url, expected, '');
  };

  /** A cached package, re-verified on the way out, or `null`. */
  const cached = async (url: string): Promise<CachedPack | null> => {
    const held = await cache.readPack(url);
    if (held === null) return null;
    if (held.sha256 === '' || !canVerify()) return held;
    // Cheap, and it catches the one thing a cache cannot otherwise notice:
    // bytes that rotted after they were stored.
    const actual = await sha256OfText(held.text);
    if (digestMatches(held.sha256, actual)) return held;
    await cache.deletePack(url);
    return null;
  };

  const parsePack = (text: string, what: string): LevelPackage => {
    const pack = JSON.parse(text) as LevelPackage;
    // The manifest is authoritative: a catalog entry can be stale or wrong, and
    // this is the number the package itself insists on.
    assertEngine(what, pack.manifest?.minEngineVersion);
    return pack;
  };

  return {
    catalog: async () => {
      try {
        const { text } = await fetchText(catalogUrl.toString(), 'the mission catalog');
        const catalog = parseCatalog(JSON.parse(text) as unknown);
        await cache.writeDocument('catalog', text, now());
        return { catalog, origin: 'network', reason: null };
      } catch (cause) {
        const reason =
          cause instanceof Error ? cause.message : 'The mission catalog could not be loaded.';
        const held = await cache.readDocument('catalog');
        if (held !== null) {
          try {
            return {
              catalog: parseCatalog(JSON.parse(held.text) as unknown),
              origin: 'cache',
              reason,
            };
          } catch {
            // A cached catalog that no longer parses is no catalog at all.
          }
        }
        // Degraded but launchable: whatever this device is holding, listed,
        // with the screens saying plainly that it may not be all of it.
        return { catalog: await catalogFromCachedPacks(cache), origin: 'device', reason };
      }
    },

    profiles: () =>
      parsed<DifficultyProfiles>('profiles', at('profiles.json'), 'the difficulty profiles'),

    perks: () => parsed<CareerPerks>('perks', at('perks.json'), 'the perk catalogue'),

    calibration: async (entry) => {
      // Absent for a mission whose calibration sweep has not run, and absent
      // offline for one never opened online. Either way that is a missing
      // duration estimate, not a failure to launch.
      try {
        return await parsed<CalibrationReport>(
          `calibration:${entry.id}`,
          at(`${entry.id}/calibration.json`),
          'the calibration report',
        );
      } catch {
        return null;
      }
    },

    pack: async (entry) => {
      const url = at(entry.url);
      assertEngine(entry.title, entry.minEngineVersion);

      const held = await cached(url);
      if (held !== null) return parsePack(held.text, entry.title);

      const { text, sha256 } = await fetchVerified(url, entry.title, entry.sha256);
      // Parsed — and therefore engine-checked — before it is stored: a package
      // this build cannot run is not one to keep a copy of.
      const pack = parsePack(text, entry.title);
      await cache.writePack({
        url,
        packId: entry.id,
        packVersion: entry.packVersion,
        text,
        sha256,
        fetchedAt: now(),
        playedAt: null,
      });
      await cache.evict();
      return pack;
    },

    packFrom: async (url, label) => {
      const resolved = at(url);
      const held = await cached(resolved);
      if (held !== null) return parsePack(held.text, label);
      // No expectation to check against: the catalog has moved on and no longer
      // names this version's digest. Published packages are immutable and
      // same-origin, so fetching one back is safe; it simply cannot be *proved*
      // the way a catalogued one can, and it is not cached as though it had been.
      const { text } = await fetchText(resolved, label);
      return parsePack(text, label);
    },

    markPlayed: async (entry) => {
      await cache.markPlayed(at(entry.url), now());
    },
  };
}

/** A source that never caches, for a device with no usable IndexedDB. */
export function uncachedContentSource(options: DeliveryOptions = {}): ContentSource {
  return deliveringContentSource({ ...options, cache: nullContentCache() });
}

/**
 * The solver's par for a profile, in sends and in phases, or `null` where the
 * search has not run for it.
 *
 * Returned as `null` rather than a guess: `quarterlyReview` treats an unknown
 * par as par met, and `createGame` falls back to the package's declared phase
 * budget, so an absent report costs the player nothing. A fabricated number
 * here would silently mis-score every run of an uncalibrated mission.
 */
export function solverParFor(
  report: CalibrationReport | null,
  profileId: string,
): { readonly sends: number | null; readonly phases: number | null } {
  if (!report?.solver?.measured) return { sends: null, phases: null };
  const row = report.solver.profiles?.find((entry) => entry.profileId === profileId);
  return {
    sends: typeof row?.parSends === 'number' ? row.parSends : null,
    phases: typeof row?.parPhases === 'number' ? row.parPhases : null,
  };
}

/** The measured estimate for a profile, in minutes, or `null` if unmeasured. */
export function estimatedMinutesFor(
  report: CalibrationReport | null,
  profileId: string,
): number | null {
  if (!report?.agent?.measured) return null;
  const row = report.agent.profiles?.find((entry) => entry.profileId === profileId);
  return typeof row?.estimatedMinutes === 'number' ? row.estimatedMinutes : null;
}

/**
 * The measured duration *range* for a profile — a typical evening and a long
 * one — which is what the difficulty screen is specified to show.
 *
 * Falls back to a point estimate where a report predates the range, and to
 * `null` where nothing has been measured. Collapses to a point where the two
 * ends round to the same minute, because "6 to 6 min" is not a range.
 */
export function estimatedMinutesRangeFor(
  report: CalibrationReport | null,
  profileId: string,
): readonly [number, number] | null {
  if (!report?.agent?.measured) return null;
  const row = report.agent.profiles?.find((entry) => entry.profileId === profileId);
  const range = row?.estimatedMinutesRange;
  const low = range?.[0];
  const high = range?.[1];
  if (typeof low === 'number' && typeof high === 'number') {
    return high > low ? [low, high] : [low, low];
  }
  const point = estimatedMinutesFor(report, profileId);
  return point === null ? null : [point, point];
}
